import type { VorankuendigungData } from '../types';

/**
 * Erläuterungen zur Vorankündigung für den Bauherrn — je Punkt ein kurzer
 * Absatz, was dort steht und worauf er achten soll.
 *
 * Stehen im Begleitblatt des PDF-Exports (`pdf/vorankBegleitblatt.ts`) und auf
 * der öffentlichen Unterschriftsseite (`#/vorankuendigung/<token>`). Die Texte
 * sind der Entwurf aus docs/vorankuendigung-unterschrift-plan.md §10 —
 * fachlich vom Betreiber zu prüfen, Änderungen nur dort und hier zugleich.
 *
 * Die Texte gehen ins PDF (Standardschrift, CP1252): keine Zeichen außerhalb
 * dieser Kodierung, keine Zeilenumbrüche (der Satz bricht selbst um).
 */
export interface VorankErlaeuterung {
    /** Nummer des Punkts in der Vorankündigung, '1' … '12'. */
    punkt: string;
    titel: string;
    text: string;
}

export const VORANK_ERLAEUTERUNGEN: readonly VorankErlaeuterung[] = [
    {
        punkt: '1',
        titel: 'Ort der Baustelle',
        text: 'Die Anschrift, unter der die Baustelle eingerichtet wird. Darüber ordnet die Behörde die '
            + 'Vorankündigung zu. Bitte Straße, Hausnummer und PLZ prüfen.',
    },
    {
        punkt: '2',
        titel: 'Bauherr',
        text: 'Sie als Auftraggeber des Vorhabens, dazu eine Person, die für Rückfragen der Behörde '
            + 'erreichbar ist. Bitte Name, Anschrift, Telefon und E-Mail prüfen.',
    },
    {
        punkt: '3',
        titel: 'Art des Vorhabens',
        text: 'Kurz, was gebaut, umgebaut oder abgerissen wird. Die Behörde schätzt daran die Gefährdungen '
            + 'auf der Baustelle ein.',
    },
    {
        punkt: '4',
        titel: 'Verantwortlicher Dritter',
        text: 'Nur ausgefüllt, wenn Sie einen Dritten (z. B. Projektsteuerer) beauftragt haben, die Pflichten '
            + 'nach der Baustellenverordnung in eigener Verantwortung wahrzunehmen (§ 4 BaustellV).',
    },
    {
        punkt: '5',
        titel: 'Koordinator',
        text: 'Der Sicherheits- und Gesundheitsschutzkoordinator, den Sie für Planung und/oder Ausführung '
            + 'bestellt haben (§ 3 BaustellV).',
    },
    {
        punkt: '6',
        titel: 'Beginn und Ende',
        text: 'Der voraussichtliche Zeitraum der Arbeiten. Die Vorankündigung muss der Behörde spätestens '
            + 'zwei Wochen vor Einrichtung der Baustelle vorliegen.',
    },
    {
        punkt: '7',
        titel: 'Beschäftigte gleichzeitig',
        text: 'Geschätzte Höchstzahl der Personen, die gleichzeitig auf der Baustelle arbeiten. Mit der Dauer '
            + 'entscheidet sie, ob eine Vorankündigung nötig ist: mehr als 30 Arbeitstage und mehr als '
            + '20 Beschäftigte gleichzeitig, oder mehr als 500 Personentage.',
    },
    {
        punkt: '8',
        titel: 'Arbeitgebende',
        text: 'Voraussichtliche Zahl der Firmen mit eigenen Beschäftigten auf der Baustelle.',
    },
    {
        punkt: '9',
        titel: 'Unternehmer ohne Beschäftigte',
        text: 'Voraussichtliche Zahl der Selbstständigen, die selbst auf der Baustelle arbeiten.',
    },
    {
        punkt: '10',
        titel: 'Bereits ausgewählte Arbeitgebende',
        text: 'Firmen, die heute schon beauftragt sind. Weitere kommen im Bauablauf hinzu.',
    },
    {
        punkt: '11',
        titel: 'Besonders gefährliche Arbeiten',
        text: 'Arbeiten nach Anhang II BaustellV, z. B. Absturzgefahr aus mehr als 7 m, Gräben oder Baugruben '
            + 'tiefer als 5 m, Sprengarbeiten, Arbeiten nahe Hochspannungsleitungen. Sie bestimmen die '
            + 'besonderen Schutzmaßnahmen im SiGe-Plan.',
    },
    {
        punkt: '12',
        titel: 'Verantwortliche Bauleitung',
        text: 'Nur dort, wo die Landesbauordnung eine verantwortliche Bauleitung verlangt (z. B. Hamburg).',
    },
];

/** Absatz „Unterschrift“ — steht nach den Punkten. */
export const VORANK_ERLAEUTERUNG_UNTERSCHRIFT =
    'Sie bestätigen als Bauherr, dass die Angaben nach Ihrem Kenntnisstand zutreffen. Ändert sich '
    + 'Wesentliches (Zeitraum, Zahl der Beschäftigten, Koordinator), ist die Vorankündigung anzupassen; '
    + 'Sie erhalten dann eine neue Fassung. Die Vorankündigung wird sichtbar auf der Baustelle ausgehängt.';

/**
 * Die Erläuterungen, die zu einer Vorankündigung gehören: Punkt 12
 * (verantwortliche Bauleitung) steht nur, wenn das Blatt ihn auch zeigt —
 * wie `generateVorankuendigungPDF`, das ihn nur bei `hasSiteManager` setzt.
 */
export const erlaeuterungenFuer = (data: Pick<VorankuendigungData, 'hasSiteManager'>): VorankErlaeuterung[] =>
    VORANK_ERLAEUTERUNGEN.filter(e => e.punkt !== '12' || data.hasSiteManager === true);
